import React, { useState, useEffect, useRef, useCallback } from "react"
import { Search, Loader2, Check, X, User } from "lucide-react"
import { Button } from "src/components/ui/button"
import { fetchDeals } from "src/features/deals/api/dealsApi"
import { formatMoney } from "src/features/deals/constants"

// ── DealPicker ───────────────────────────────────────────────────────────────
// A searchable dropdown of open deals for a project, used when blocking or
// booking a unit so the change is tied to the deal it's being held for. Deals
// are fetched from /api/v1/deals (filtered by project + the search query) and
// each row shows the info a sales user needs to disambiguate: deal name, the
// lead it came from, its stage and value.
export function DealPicker({ projectId, value, onChange, disabled }) {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef(null)

  // Debounced fetch of open deals for this project, narrowed by the query.
  useEffect(() => {
    if (!projectId) return
    let active = true
    setLoading(true)
    const t = setTimeout(() => {
      fetchDeals({ project_id: projectId, status: "open", q: query.trim(), per_page: 20 })
        .then((d) => { if (active) setDeals(d.deals || []) })
        .catch(() => { if (active) setDeals([]) })
        .finally(() => { if (active) setLoading(false) })
    }, 200)
    return () => { active = false; clearTimeout(t) }
  }, [projectId, query])

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  const select = useCallback((deal) => {
    onChange(deal)
    setOpen(false)
    setQuery("")
  }, [onChange])

  // Selected-state summary chip — shown instead of the search input once a
  // deal is chosen, with a clear button to pick a different one.
  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-slate-800">{value.name}</p>
          <p className="truncate text-[12px] text-slate-500">
            {value.lead?.name || "No lead"}
            {value.value != null && ` · ${formatMoney(value.value)}`}
            {value.stage?.name && ` · ${value.stage.name}`}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onChange(null)}
          disabled={disabled}
          className="h-auto w-auto shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-50"
          aria-label="Clear selected deal"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white pl-3 pr-3 shadow-sm transition focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
        <Search className="h-4 w-4 shrink-0 text-slate-400" />
        <input
          type="text"
          className="h-full w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Search deals…"
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-[13px] text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading deals…
            </div>
          ) : deals.length === 0 ? (
            <div className="px-3 py-6 text-center text-[13px] text-slate-500">
              {query ? "No matching deals" : "No open deals for this project"}
            </div>
          ) : (
            deals.map((deal) => {
              const selected = value?.id === deal.id
              return (
                <Button
                  key={deal.id}
                  type="button"
                  variant="ghost"
                  onClick={() => select(deal)}
                  className="flex h-auto w-full items-start justify-start gap-2 rounded-none px-3 py-2 text-left font-normal hover:bg-slate-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-slate-800">{deal.name}</p>
                    <p className="flex items-center gap-1 truncate text-[12px] text-slate-500">
                      <User className="h-3 w-3 shrink-0" />
                      {deal.lead?.name || "No lead"}
                      {deal.value != null && ` · ${formatMoney(deal.value)}`}
                      {deal.stage?.name && ` · ${deal.stage.name}`}
                    </p>
                  </div>
                  {selected && <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
                </Button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
